import { apiService } from '../api/ApiService';

const API_BASE = '/api/public/game-languages';

type GameLanguageLanguage = {
    id: string;
    name: string;
    isoName: string;
    flag: string;
    order: number;
};

export type GameLanguage = {
    id: string;
    gameId: string;
    languageId: string;
    enabled: boolean;
    mechanic: string | null;
    bet: string | null;
    maxBet: string | null;
    minBet: string | null;
    genres: string | null;
    category: string | null;
    comingSoonTitle: string | null;
    comingSoonBrief: string | null;
    lobbyDescription: string | null;
    readMoreDescription: string | null;
    gameStory: string | null;
    gameFeatures1: string | null;
    gameFeatures2: string | null;
    gameFeatures3: string | null;
    gameFeatures1Title: string | null;
    gameFeatures2Title: string | null;
    gameFeatures3Title: string | null;
    createdAt: string;
    updatedAt: string;
    language: GameLanguageLanguage;
};

type PublicGameLanguageResponse = {
    gameLanguage: GameLanguage;
};

type PublicAvailableGameLanguagesResponse = {
    languages: GameLanguageLanguage[];
};

type PublicGameLanguagesByLocaleResponse = {
    gameLanguages: Array<{
        gameId: string;
        gameLanguage: GameLanguage;
    }>;
};

class GameLanguagesService {
    public async getLiveGameLanguage(gameSystemName: string, locale: string): Promise<GameLanguage> {
        const params = new URLSearchParams({
            gameId: gameSystemName,
            locale,
        });

        const payload = await apiService.request<PublicGameLanguageResponse>(
            `${API_BASE}?${params.toString()}`
        );

        if (!payload?.gameLanguage) {
            throw new Error('Invalid public game language API shape');
        }

        return payload.gameLanguage;
    }

    public async getLiveAvailableLanguages(gameSystemName: string): Promise<GameLanguageLanguage[]> {
        const params = new URLSearchParams({
            gameId: gameSystemName,
        });

        const payload = await apiService.request<PublicAvailableGameLanguagesResponse>(
            `${API_BASE}/available?${params.toString()}`
        );

        if (!payload || !Array.isArray(payload.languages)) {
            throw new Error('Invalid public available game languages API shape');
        }

        return payload.languages;
    }

    public async getLiveGameLanguagesByLocale(
        locale: string
    ): Promise<Record<string, GameLanguage>> {
        return this.loadGameLanguagesByLocale(`${API_BASE}/by-locale`, locale);
    }

    public async getDraftGameLanguagesByLocale(
        locale: string
    ): Promise<Record<string, GameLanguage>> {
        return this.loadGameLanguagesByLocale(`${API_BASE}/by-locale/preview`, locale);
    }

    private async loadGameLanguagesByLocale(
        url: string,
        locale: string
    ): Promise<Record<string, GameLanguage>> {
        const params = new URLSearchParams({ locale });
        const payload = await apiService.request<PublicGameLanguagesByLocaleResponse>(
            `${url}?${params.toString()}`
        );

        if (!payload || !Array.isArray(payload.gameLanguages)) {
            throw new Error('Invalid public game languages by locale API shape');
        }

        return Object.fromEntries(
            payload.gameLanguages.map((entry) => [entry.gameId, entry.gameLanguage])
        );
    }
}

export const gameLanguagesService = new GameLanguagesService();
